/**
 * Catalog import types — mediates between the Temporal workflow backend and
 * the simple three-state UI the Complete Import screen renders.
 *
 * Flow:
 *   1. POST  /products/sync              → returns `workflowId`
 *   2. GET   /workflows/:workflowId/status → polled for progress
 */

// ---------------------------------------------------------------------------
// Backend: POST /products/sync  → wrapped in ApiResponse<TriggerSyncResponse>
// ---------------------------------------------------------------------------

/** Body for `POST /products/sync`. `connection_id` is optional server-side but
 *  we always pass it — we know the GMC connection at the call site.
 *
 *  Scoped mode: a non-empty `productIds` switches the same endpoint to import
 *  exactly those products (GMC additionally starts per-product analytics legs).
 *  Supported by the `SCOPED_IMPORT_SOURCES` connectors (GMC + Meta Commerce).
 *  Omit `productIds` for the legacy full-catalog sync. */
export interface TriggerSyncRequest {
  connector_code: string;
  connection_id: string;
  /** Verbatim opaque `id`s from `/sync/products/list`, ≤ 50. Echo unchanged —
   *  a reconstructed/mis-framed id is silently dropped from the import. */
  productIds?: string[];
  /** Analytics window start (`YYYY-MM-DD`). Optional; backend defaults to `until − 30d`. */
  since?: string;
  /** Analytics window end (`YYYY-MM-DD`). Optional; backend defaults to yesterday (UTC). */
  until?: string;
}

/** A single Temporal workflow reference returned for a scoped-mode analytics leg. */
export interface WorkflowRef {
  runId: string;
  workflowId: string;
}

/** Scoped-mode analytics workflow refs. Either leg is `null` when its
 *  connection is absent or the (best-effort) start failed — treat missing
 *  refs as "analytics not available", not an error. */
export interface ScopedAnalyticsRefs {
  gmc: WorkflowRef | null;
  googleAds: WorkflowRef | null;
}

export interface TriggerSyncResponse {
  jobId?: string;
  runId: string;
  pipelineId: string;
  workflowId: string;
  connectionId: string;
  connectorCode: string;
  maxProducts: number | null;
  status: string;
  /** Scoped mode only (`null` in legacy full-catalog mode). Not polled in the
   *  current iteration — the products-import `workflowId` above is primary. */
  analytics?: ScopedAnalyticsRefs | null;
}

/** Structured `detail.error_code`s the scoped-import guards return (surfaced as
 *  `ApiError.serverCode`). Client-side caps/gating should prevent both. */
export type ScopedImportErrorCode = 'PRODUCT_CAP_EXCEEDED' | 'SCOPED_IMPORT_UNSUPPORTED_CONNECTOR';

/** Hard cap on products per scoped import (contract §2). Enforced client-side
 *  before the call so the user never hits `PRODUCT_CAP_EXCEEDED`. */
export const SCOPED_IMPORT_MAX_PRODUCTS = 50;

// ---------------------------------------------------------------------------
// Backend: GET /workflows/:id/status  → wrapped in ApiResponse<WorkflowStatusResponse>
// ---------------------------------------------------------------------------

/** Temporal-mapped workflow status enum (matches backend WorkflowStatus). */
export type WorkflowStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'PAUSED'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

/** Timing sub-object present while RUNNING/PAUSED.
 *
 *  Snake_case because it's a passthrough of the Temporal workflow's
 *  `get_progress` query dict — the backend types it as `dict[str, Any]`
 *  and does NOT run it through Pydantic field aliases. */
export interface WorkflowTimingInfo {
  elapsed_seconds: number;
  products_per_second: number;
  avg_seconds_per_page: number;
  estimated_seconds_remaining: number | null;
  started_at: string;
  updated_at: string;
}

/** Shape of `progress` for sync workflows — snake_case (see TimingInfo note).
 *  Other workflow types (delivery, analytics) have different shapes; we only
 *  care about sync-products here.
 *
 *  Only `products_processed` and `percent_complete` are guaranteed: the
 *  backend now also attaches a reduced `progress` object on COMPLETED
 *  (previously `progress` was dropped entirely once terminal), and that
 *  terminal payload omits `products_failed`, `pages_completed`,
 *  `estimated_total`, and `timing`. */
export interface WorkflowSyncProgress {
  products_processed: number;
  products_failed?: number;
  pages_completed?: number;
  percent_complete: number | null;
  estimated_total?: number | null;
  timing?: WorkflowTimingInfo;
}

/** One stage of the sync workflow (load_context → start_pipeline →
 *  sync_products → complete_pipeline). We only read `startedAt` (first stage)
 *  and `completedAt` (last stage) to compute total elapsed time on COMPLETED;
 *  the rest is surface area for future stage-level UX. */
export interface WorkflowStage {
  name: string;
  label: string;
  status: WorkflowStatus;
  startedAt: string;
  completedAt: string | null;
  detail: unknown;
}

/** Terminal payload on COMPLETED. Only the fields the UI binds to are typed —
 *  the backend also returns a rich `data_quality` breakdown that other screens
 *  will eventually consume; leaving it untyped here keeps the surface tight. */
export interface WorkflowSyncResult {
  /** Total products processed by the sync run — drives the "Total Products" stat. */
  total_synced: number;
  /** Rows successfully written to ClickHouse — drives the "Imported" stat. */
  ch_rows_written: number;
  ch_write_failures?: number;
  records_failed?: number;
  status?: string;
  sync_id?: string;
  connection_id?: string;
  // Rich quality breakdown — not consumed here.
  data_quality?: Record<string, unknown>;
}

export interface WorkflowStatusResponse {
  workflowId: string;
  status: WorkflowStatus;
  stages?: WorkflowStage[];
  progress?: WorkflowSyncProgress | null;
  result?: WorkflowSyncResult | null;
  error?: string | null;
  /** `pipeline_runs.id` backing this workflow — keys the run lineage/pages/events
   *  reads. Absent on backends that predate the ingest-run-controls contract. */
  pipelineRunId?: string | null;
  /** Not in the v1 status contract (lineage carries it); read defensively. */
  errorCode?: string | null;
  /** FAILED only — false when a resume cannot help (e.g. bad credentials). */
  retryable?: boolean | null;
  /** FAILED only — plain-English, customer-safe copy owned by the backend. */
  userMessage?: string | null;
  /** FAILED only — absolute page number the run stopped on. */
  failedPage?: number | null;
}

// ---------------------------------------------------------------------------
// UI-facing shape — flattened tri-state view the screen already renders.
// ---------------------------------------------------------------------------

export type CatalogImportStatus = 'in_progress' | 'completed' | 'failed';

/** Five-way run phase the run-controls card renders (PENDING folds into running). */
export type CatalogImportPhase = 'running' | 'paused' | 'failed' | 'cancelled' | 'completed';

export interface CatalogImportView {
  workflowId: string;
  source: string;
  status: CatalogImportStatus;
  totalProducts: number;
  importedProducts: number;
  percentComplete: number;
  estimatedTimeRemainingSeconds: number | null;
  completionTimeFormatted?: string;
  failureReason?: string;
  // -- Ingest run controls (all optional so older payloads/fixtures still map) --
  /** Five-way phase; drives the run-controls card and its actions. */
  phase?: CatalogImportPhase;
  pipelineRunId?: string | null;
  errorCode?: string | null;
  retryable?: boolean | null;
  userMessage?: string | null;
  failedPage?: number | null;
  /** Pages completed by this run (live progress). */
  pagesCompleted?: number | null;
  /** Workflow stages for the Overview tab. */
  stages?: WorkflowStage[];
}
